import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { startLoadingDispositivosPos } from '../../../actions/pos';
import { RESOLUCION_ERROR, RESOLUCION_RESUELTA } from '../../../helpers/cobroDatafono';

/**
 * EX-595: asociación de datáfonos físicos (POSMS) a la sucursal.
 *
 * No hay CRUD de dispositivos: los provisiona soporte de Paynopain y Exora solo gestiona la
 * asociación, que viaja en `Sucursal.datafonos` con el resto del formulario.
 *
 * Desactivar todos los datáfonos apaga la integración en esta sucursal: el cobro con "Pago
 * Tarjeta" vuelve al registro manual de la venta.
 *
 * EX-614: la asociación NO sale del store de sucursales (su endpoint es público y nunca proyecta
 * `datafonos`), sino del mismo endpoint autenticado que usa el enrutado del cobro. La pantalla la
 * recibe ya resuelta desde `Sucursal.js` junto con su resolución.
 */

export const MENSAJE_CONFIGURACION_NO_LEIDA = 'No se ha podido leer la configuración de datáfonos '
    + 'de esta sucursal, así que no se muestra ninguno. Vuelve a comprobarlo para poder cambiarla: '
    + 'al guardar, la asociación que haya se conserva tal cual.';

export const MENSAJE_CONFIGURACION_CARGANDO = 'Comprobando los datáfonos asociados a esta sucursal...';

/**
 * EX-619: mismo texto que devuelve el backend cuando la sucursal no tiene comercio, para que el
 * aviso no cambie según se haya llegado a llamar al endpoint o no.
 */
export const MENSAJE_SIN_COMERCIO = 'La sucursal no tiene comercio POSMS configurado';

/**
 * EX-619: no es lo mismo que el anterior. Ahí se sabe que la sucursal no tiene comercio; aquí no
 * se ha podido leer cuál es, así que el campo sale vacío sin que eso signifique nada. Al guardar,
 * el comercio no viaja en el cuerpo y lo configurado se queda como está.
 */
export const MENSAJE_COMERCIO_NO_LEIDO = 'No se ha podido leer el comercio POSMS de esta sucursal, '
    + 'así que no se muestran sus datáfonos. Vuelve a comprobarlo para poder configurarlos: al '
    + 'guardar, el comercio que haya se conserva tal cual.';

/**
 * EX-619: el comercio POSMS es un UUID. Comprobar la forma antes de pedir la lista evita una
 * petición por cada tecla mientras se escribe, y un comercio a medio teclear no es un comercio.
 */
const FORMA_COMERCIO = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export const esComercioConForma = (comercio) => FORMA_COMERCIO.test(String(comercio || '').trim());

const nombreDeDispositivo = (dispositivo) =>
    dispositivo.identifier || dispositivo.merchant_name || dispositivo.id;

/**
 * Lo que Paylands cuenta del terminal además de su identificador: la descripción que le puso
 * soporte y el número de serie, que es lo que está impreso en el aparato físico. El endpoint
 * reenvía el dispositivo entero, así que no hace falta pedirlo aparte.
 */
export const detalleDePaylands = (dispositivo = {}) => [
    dispositivo.description,
    dispositivo.serial_number && `Nº de serie ${dispositivo.serial_number}`,
].filter(Boolean).join(' · ');

/**
 * EX-614: por qué un datáfono no se puede asociar aquí. Con el nombre de la sucursal cuando se
 * sabe: quien lo quiera en este mostrador tiene que saber de dónde quitarlo primero.
 */
export const textoAsignadoEnOtraSucursal = (nombreSucursal) => nombreSucursal
    ? `Asignado a ${nombreSucursal}`
    : 'Asignado a otra sucursal';

/** Duplicidad ya guardada: se avisa, pero no se bloquea. */
export const textoTambienAsignado = (nombreSucursal) => nombreSucursal
    ? `También asignado a ${nombreSucursal}`
    : 'También asignado a otra sucursal';

/**
 * Une los dispositivos que devuelve la API con los ya asociados. Un datáfono asociado que la API
 * deja de devolver sigue mostrándose: si desapareciera de la lista, el usuario no podría quitarlo
 * ni entendería por qué la sucursal sigue cobrando con él.
 *
 * EX-614: manda el nombre que le haya puesto el comercio, porque el de Paylands puede ser un
 * identificador ilegible (en el terminal de pruebas es un UUID) y es el que acaba viendo el
 * operario en el selector del cobro. El del dispositivo viaja aparte para poder seguir mostrándolo:
 * quien renombra necesita saber a qué terminal físico le está poniendo "Mostrador".
 */
export const combinarDispositivos = (dispositivos = [], datafonos = [], asignadosEnOtras = new Map(), sucursalId = null) => {
    const asignadaEnElMapa = (deviceId) => asignadosEnOtras?.get?.(deviceId) ?? null;

    /**
     * EX-619: en qué OTRO mostrador está el terminal.
     *
     * Manda el `asociadoA` que devuelve el propio endpoint, no el mapa que arma la pantalla con
     * la configuración cargada al abrir: al teclear un comercio distinto, ese mapa está calculado
     * sobre los terminales del comercio anterior y uno ocupado se vería libre. El del endpoint
     * viaja con la respuesta, acotado a las sucursales de la empresa de esta sucursal.
     *
     * La propia sucursal se descarta: el endpoint la incluye en la ocupación, y tomarla por "otro
     * mostrador" bloquearía el terminal que se acaba de desasociar en el formulario.
     *
     * El mapa se conserva para las filas que el endpoint no devuelve —los huérfanos, y una
     * respuesta de un backend que todavía no mandara la clave—, no como respaldo de un `null`
     * explícito: ese `null` significa libre y es autoritativo.
     */
    const asignadaA = (deviceId, asociadoA) => {
        // En un alta la consulta va sin sucursal, y sin ella el backend no tiene empresa contra la
        // que resolver la ocupación: manda TODOS los terminales con `asociadoA: null`. Ahí ese
        // `null` no significa "libre" sino "no lo sé", así que manda el mapa; si no, una sucursal
        // nueva ofrecería como libre lo que ya está en otro mostrador (justo lo que fijó EX-614).
        if (!sucursalId) return asignadaEnElMapa(deviceId);
        if (asociadoA === undefined) return asignadaEnElMapa(deviceId);
        if (!asociadoA) return null;
        if (String(asociadoA._id) === String(sucursalId)) return null;
        return asociadoA.nombre || '';
    };

    const filas = dispositivos.map(dispositivo => {
        const asociacion = datafonos.find(datafono => datafono.deviceId === dispositivo.id) || null;
        const nombreDispositivo = nombreDeDispositivo(dispositivo);
        return {
            deviceId: dispositivo.id,
            nombre: asociacion?.nombre || nombreDispositivo || dispositivo.id,
            nombreDispositivo,
            detallePaylands: detalleDePaylands(dispositivo),
            asociacion,
            asignadaA: asignadaA(dispositivo.id, dispositivo.asociadoA),
        };
    });
    const huerfanos = datafonos
        .filter(datafono => !dispositivos.some(dispositivo => dispositivo.id === datafono.deviceId))
        .map(datafono => ({
            deviceId: datafono.deviceId,
            nombre: datafono.nombre || datafono.deviceId,
            nombreDispositivo: null,
            detallePaylands: '',
            asociacion: datafono,
            asignadaA: asignadaEnElMapa(datafono.deviceId),
        }));
    return [...filas, ...huerfanos];
};

/**
 * EX-614: ¿se puede asociar esta fila? Solo se bloquea la que está asignada a otra sucursal y NO
 * a esta.
 *
 * La asignación propia manda aunque el mismo terminal figure también en otra: es la única forma
 * de poder quitarlo, y bloquearla dejaría atrapadas las duplicidades que ya existan en base de
 * datos (nada las impedía antes de esta pantalla).
 */
export const estaAsignadoEnOtraSucursal = (fila) => !fila?.asociacion && !!fila?.asignadaA;

/** EX-614: cambia el nombre de un datáfono asociado sin tocar el resto de la lista. */
export const renombrarDatafono = (datafonos = [], deviceId, nombre) =>
    datafonos.map(datafono => datafono.deviceId === deviceId
        ? { ...datafono, nombre }
        : datafono);

/**
 * EX-614: qué asociación viaja en el guardado de la sucursal.
 *
 * El backend actualiza con un `$set` de las claves presentes en el body, así que omitir
 * `datafonos` cuando la configuración no se ha podido leer deja intacta la asociación guardada.
 * Mandar en ese caso la lista vacía de la pantalla borraría los terminales del comercio.
 *
 * La clave que pudiera venir del store se descarta siempre: ese endpoint es público y nunca trae
 * la asociación real.
 */
export const datafonosParaGuardar = (sucursal = {}, { resolucion, datafonos = [] } = {}) => {
    const sucursalSinDatafonos = { ...sucursal };
    delete sucursalSinDatafonos.datafonos;
    if (resolucion !== RESOLUCION_RESUELTA) return sucursalSinDatafonos;
    return { ...sucursalSinDatafonos, datafonos };
};

export const DatafonosSucursal = ({
    datafonos = [],
    asignadosEnOtras = new Map(),
    resolucion,
    reintentar,
    editMode,
    onChange,
    comercio = '',
    comercioPendiente = false,
    comercioNoLeido = false,
    reintentarComercio,
    comercioGestionado = false,
    sucursalId = null,
}) => {
    const dispatch = useDispatch();
    const { colores } = useSelector(state => state.ui);

    const [dispositivos, setDispositivos] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [aviso, setAviso] = useState('');
    const [sinComercio, setSinComercio] = useState(false);

    // EX-619: el comercio que manda es el del formulario, no el guardado, y por eso se pide la
    // lista contra él. Se espera a que tenga forma de UUID para no disparar una petición por cada
    // tecla; a medio teclear la sección se queda como "sin comercio", que es lo que es.
    const comercioConsultable = esComercioConForma(comercio) ? comercio.trim() : '';

    useEffect(() => {
        // Un rol de negocio no ve el comercio y no puede conducir la lista con él: se pide por
        // sucursal y decide el backend con el que tenga guardado, que es lo que se hacía antes de
        // este ticket. Solo un rol de plataforma tiene un comercio candidato que ofrecer.
        // Mientras no se sepa cuál es el comercio guardado no se pinta nada: darlo por inexistente
        // haría parpadear el aviso al abrir cualquier sucursal que sí lo tenga.
        if (comercioGestionado && comercioPendiente) {
            setDispositivos([]);
            setAviso('');
            setSinComercio(false);
            setCargando(true);
            return;
        }

        // Hay algo que preguntar si se tiene un comercio candidato, o una sucursal guardada contra
        // cuyo comercio resuelva el backend. Sin ninguna de las dos —el alta de una sucursal a la
        // que todavía no se le ha puesto comercio— no se llama al endpoint: no hay terminales que
        // listar y preguntar sin parámetros solo traería su error de "falta la sucursal".
        const hayAlgoQueConsultar = !!comercioConsultable || (!comercioGestionado && !!sucursalId);
        if (!hayAlgoQueConsultar) {
            setDispositivos([]);
            setAviso(MENSAJE_SIN_COMERCIO);
            setSinComercio(true);
            setCargando(false);
            return;
        }

        let vigente = true;
        setCargando(true);
        const cargar = async () => {
            const resp = await dispatch(startLoadingDispositivosPos({
                sucursal: sucursalId,
                merchant: comercioGestionado ? comercioConsultable : '',
            }));
            // Al cambiar el comercio antes de que conteste la petición anterior, la que llega
            // tarde no puede pintar los terminales del comercio que ya no está en el formulario.
            if (!vigente) return;
            setDispositivos(resp.ok ? resp.dispositivos : []);
            setAviso(resp.msg || '');
            // Con la respuesta correcta, el `msg` solo llega cuando la sucursal no tiene comercio
            // configurado: es como se entera un rol de negocio, que no lo ve en el formulario.
            setSinComercio(!!resp.ok && !!resp.msg);
            setCargando(false);
        };
        cargar();
        return () => { vigente = false; };
    }, [dispatch, comercioConsultable, comercioPendiente, comercioGestionado, sucursalId]);

    const alternarAsociacion = (fila) => {
        // El `disabled` del input no es la garantía: la comprobación va aquí para que ninguna
        // otra vía (teclado, un futuro botón) pueda asociar un terminal que está en otro sitio.
        if (estaAsignadoEnOtraSucursal(fila)) return;
        const yaAsociado = !!fila.asociacion;
        if (yaAsociado) {
            onChange(datafonos.filter(datafono => datafono.deviceId !== fila.deviceId));
            return;
        }
        // El primero que se asocia queda como predeterminado: una sucursal con datáfonos siempre
        // tiene uno con el que cobrar sin preguntar.
        onChange([...datafonos, {
            deviceId: fila.deviceId,
            nombre: fila.nombreDispositivo || fila.deviceId,
            predeterminado: datafonos.length === 0,
            activo: true,
        }]);
    };

    const cambiarNombre = (deviceId, nombre) => {
        onChange(renombrarDatafono(datafonos, deviceId, nombre));
    };

    const marcarPredeterminado = (deviceId) => {
        onChange(datafonos.map(datafono => ({
            ...datafono,
            predeterminado: datafono.deviceId === deviceId,
        })));
    };

    const alternarActivo = (deviceId) => {
        onChange(datafonos.map(datafono => datafono.deviceId === deviceId
            ? { ...datafono, activo: !datafono.activo }
            : datafono));
    };

    // EX-619: sin comercio en el formulario no se pinta ninguna fila, ni siquiera los terminales
    // ya asociados. Es lo mismo que hace el backend al ocultarlos: la asociación sigue guardada y
    // vuelve entera —nombres, predeterminado y activo— en cuanto se configure otra vez el
    // comercio, pero mientras tanto esta sucursal no cobra con ellos y no hay nada que ofrecer.
    const filas = sinComercio
        ? []
        : combinarDispositivos(dispositivos, datafonos, asignadosEnOtras, sucursalId);
    const algunoActivo = !sinComercio && datafonos.some(datafono => datafono.activo);

    // EX-619: no se ha podido leer el comercio guardado. Es distinto de "no tiene comercio" y se
    // dice, porque el campo del formulario está vacío por no saberlo, no por estar sin configurar:
    // dar a guardar así no lo borra —la clave no viaja—, pero tampoco se puede administrar hasta
    // volver a leerlo.
    if (comercioNoLeido) {
        return (
            <div className='datafonos-sucursal'>
                <h3 style={{ color: colores.colorSecundario }}>Datáfonos</h3>
                <p className='datafonos-sucursal__nota datafonos-sucursal__nota--error'>
                    {MENSAJE_COMERCIO_NO_LEIDO}
                </p>
                <button
                    type='button'
                    className='datafonos-sucursal__reintentar'
                    onClick={reintentarComercio}
                >
                    Volver a comprobar
                </button>
            </div>
        );
    }

    // EX-614: mientras no se sepa qué tiene asociado la sucursal no se pinta ninguna fila. Pintar
    // el catálogo de Paylands con todo sin marcar es exactamente la apariencia de "sucursal sin
    // datáfonos", y cualquier clic se perdería al guardar, que omite la clave sin resolución.
    if (resolucion !== RESOLUCION_RESUELTA) {
        return (
            <div className='datafonos-sucursal'>
                <h3 style={{ color: colores.colorSecundario }}>Datáfonos</h3>
                {resolucion === RESOLUCION_ERROR
                    ? (
                        <>
                            <p className='datafonos-sucursal__nota datafonos-sucursal__nota--error'>
                                {MENSAJE_CONFIGURACION_NO_LEIDA}
                            </p>
                            <button
                                type='button'
                                className='datafonos-sucursal__reintentar'
                                onClick={reintentar}
                            >
                                Volver a comprobar
                            </button>
                        </>
                    )
                    : <p className='datafonos-sucursal__nota'>{MENSAJE_CONFIGURACION_CARGANDO}</p>
                }
            </div>
        );
    }

    return (
        <div className='datafonos-sucursal'>
            <h3 style={{ color: colores.colorSecundario }}>Datáfonos</h3>

            {cargando && <p className='datafonos-sucursal__nota'>Cargando datáfonos...</p>}

            {!cargando && aviso && <p className='datafonos-sucursal__nota'>{aviso}</p>}

            {!cargando && !aviso && filas.length === 0 && (
                <p className='datafonos-sucursal__nota'>
                    No hay datáfonos disponibles para esta empresa. Los provisiona el soporte de Paynopain.
                </p>
            )}

            {filas.map(fila => {
                const asociado = !!fila.asociacion;
                const enOtraSucursal = estaAsignadoEnOtraSucursal(fila);
                const muestraNombreDelDispositivo = !!fila.nombreDispositivo
                    && fila.nombreDispositivo !== fila.nombre;
                return (
                    <div key={fila.deviceId} className='datafonos-sucursal__fila'>
                        <label className='datafonos-sucursal__nombre'>
                            <input
                                type='checkbox'
                                checked={asociado}
                                disabled={!editMode || enOtraSucursal}
                                onChange={() => alternarAsociacion(fila)}
                            />
                            <span>{fila.nombre}</span>
                        </label>

                        {/* Fuera del label a propósito: dentro cambiaría el nombre accesible de
                            la casilla, que es el que ve el operario. */}
                        {fila.detallePaylands && (
                            <span className='datafonos-sucursal__paylands'>
                                {fila.detallePaylands}
                            </span>
                        )}

                        {/* Un terminal está en un solo mostrador: se muestra, pero bloqueado y
                            diciendo dónde está. Ocultarlo dejaría al operario buscando un aparato
                            que sí existe, y a una sucursal sin ninguno con el cartel de "no hay
                            datáfonos para esta empresa". */}
                        {enOtraSucursal && (
                            <span className='datafonos-sucursal__asignado'>
                                {textoAsignadoEnOtraSucursal(fila.asignadaA)}
                            </span>
                        )}

                        {/* Duplicidad anterior a esta regla: sigue siendo editable aquí, que es
                            la única forma de deshacerla. */}
                        {asociado && fila.asignadaA && (
                            <span className='datafonos-sucursal__asignado'>
                                {textoTambienAsignado(fila.asignadaA)}
                            </span>
                        )}

                        {asociado && (
                            <div className='datafonos-sucursal__renombrar'>
                                <input
                                    type='text'
                                    className='datafonos-sucursal__input-nombre'
                                    autoComplete='off'
                                    placeholder='Nombre del datáfono'
                                    aria-label={`Nombre del datáfono ${fila.nombreDispositivo || fila.deviceId}`}
                                    value={fila.asociacion.nombre || ''}
                                    disabled={!editMode}
                                    onChange={(e) => cambiarNombre(fila.deviceId, e.target.value)}
                                />
                                {/* El nombre con el que el terminal figura en Paylands: al
                                    renombrarlo es lo único que dice a qué aparato físico se le
                                    está poniendo "Mostrador". */}
                                {muestraNombreDelDispositivo && (
                                    <span className='datafonos-sucursal__dispositivo'>
                                        {fila.nombreDispositivo}
                                    </span>
                                )}
                            </div>
                        )}

                        {asociado && (
                            <div className='datafonos-sucursal__opciones'>
                                <label>
                                    <input
                                        type='radio'
                                        name='datafono-predeterminado'
                                        checked={!!fila.asociacion.predeterminado}
                                        disabled={!editMode}
                                        onChange={() => marcarPredeterminado(fila.deviceId)}
                                    />
                                    <span>Predeterminado</span>
                                </label>
                                <label>
                                    <input
                                        type='checkbox'
                                        checked={!!fila.asociacion.activo}
                                        disabled={!editMode}
                                        onChange={() => alternarActivo(fila.deviceId)}
                                    />
                                    <span>Activo</span>
                                </label>
                            </div>
                        )}
                    </div>
                );
            })}

            {!cargando && !sinComercio && datafonos.length > 0 && !algunoActivo && (
                <p className='datafonos-sucursal__nota'>
                    Sin ningún datáfono activo, el cobro con tarjeta se sigue registrando a mano.
                </p>
            )}
        </div>
    );
};
